import React from 'react';
import classnames from 'classnames';
import Image from 'rc-image';
import ARightClickToJump from '@intelligence/components/ARightClickToJump';
import ColumnAligner from '@intelligence/v3/components/TableComponents/ColumnAligner';
import { getCountryFlag } from '@home/_config/countries';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { getHeaderWidthIntelligence, getHeaderIntelligence, getSimpleSortableHeaderIntelligence } from './utils';
import { LANGUAGE_EN } from '@home/_config/config';

import type { TableColumn } from 'tea-component';
import type { IColumnBaseOption, IColumnHeaderOption } from './utils';

import './country.scss';

import DEFAULT_COUNTRY_IMG from '@intelligence/assets/svg/flag/default.svg';

// Country Column构建器自定义配置
interface ICountryColumnBuilderOptionCore<T> {
  // 列单元格的className
  className?: string;
  // 占位符
  placeholder?: IPlaceholder;
  // 国家编码字段
  countryCodeKey?: string;
  // 国家英文名字段
  countryNameENKey?: string;
  // 国家中文名字段
  countryNameCNKey?: string;
  // 国家国旗图片字段（优先根据这个字段加载国旗图片，为空时，则组件内部根据国家编码调用getCountryFlag方法加载国旗）
  countryImgKey?: string;
  // 国家映射（系统全局获取的全量国家列表转换的国家映射，包含了国家的地区中英文名、国家的中英文名称）(如果有传countries，那么国家的中英文名称通过countries列表中的中英文决定，不看countryNameENKey， countryNameCNKey)
  countryMap?: IRecord<IRegionCountryEntity> | null;
  // 生成跳转链接
  getLinkUrl?: (cvm: T) => string;
  // 国家是否支持点击行为
  clickable?: boolean;
  // 国家点击的回调方法
  onClick?: (payload: string, cvm: T) => void;
}

type ICountryColumnBuilderOption<T> = Partial<TableColumn<T>> &
IColumnBaseOption &
IColumnHeaderOption &
ICountryColumnBuilderOptionCore<T>;

function getCountryNames<T>(
  cvm?: T,
  params?: {
    countryCodeKey?: string;
    countryNameENKey?: string;
    countryNameCNKey?: string;
    countryMap?: IRecord<IRegionCountryEntity> | null;
  },
) {
  const countryCodeKey = params?.countryCodeKey || '';
  const countryNameENKey = params?.countryNameENKey || '';
  const countryNameCNKey = params?.countryNameCNKey || '';
  const countryMap = params?.countryMap;

  if (!countryMap) {
    return {
      // @ts-ignore
      countryNameEN: cvm?.[countryNameENKey] || '',
      // @ts-ignore
      countryNameCN: cvm?.[countryNameCNKey] || '',
    };
  }

  // @ts-ignore
  const countryCode = cvm?.[countryCodeKey]?.toLowerCase() || '';
  const country = countryMap[countryCode];
  return {
    countryNameEN: country?.country_name_en || '',
    countryNameCN: country?.country_name_cn || '',
  };
}

/**
 * Country Column构建器
 * @param countryColumnBuilderOption Country Column构建器配置
 * @param countryColumnBuilderOption.className 列单元格的className
 * @param countryColumnBuilderOption.placeholder 占位符
 * @param countryColumnBuilderOption.countryCodeKey 国家编码字段
 * @param countryColumnBuilderOption.countryNameENKey 国家英文名字段
 * @param countryColumnBuilderOption.countryNameCNKey 国家英中名字段
 * @param countryColumnBuilderOption.countryImgKey 国家国旗图片字段
 * @param countryColumnBuilderOption.countryMap 全量国家映射
 * @param countryColumnBuilderOption.getLinkUrl 生成跳转链接
 * @param countryColumnBuilderOption.clickable 国家是否支持点击行为
 * @param countryColumnBuilderOption.onClick 国家点击的回调方法
 */
const CountryColumnBuilder = <T extends IRecord = IRecord>(
  countryColumnBuilderOption?: ICountryColumnBuilderOption<T>,
): TableColumn<T> => {
  const {
    className,
    placeholder = '-',
    locale = LANGUAGE_EN,
    key = 'market',
    countryCodeKey = 'market',
    countryNameENKey = 'country_name_en',
    countryNameCNKey = 'country_name_cn',
    countryImgKey = 'country_img',
    countryMap,
    headerTranslatable = true,
    headerTranslateKey = 'dbi_market',
    header = 'Market',
    fixed,
    align = 'left',
    width = 250,
    widthCN,
    getLinkUrl,
    clickable = true,
    onClick,
    ...sortOption
  } = countryColumnBuilderOption || {};

  const headerWidth = getHeaderWidthIntelligence(locale, { width, widthCN });
  const headerText = getHeaderIntelligence(locale, { headerTranslatable, headerTranslateKey, header });

  return {
    key,
    align,
    fixed,
    width: headerWidth,
    header: (
      <ColumnAligner align={align} width={headerWidth}>
        {getSimpleSortableHeaderIntelligence({ ...sortOption, key, align, header: headerText })}
      </ColumnAligner>
    ),
    render: (cvm) => {
      const countryCode = cvm?.[countryCodeKey] || '';
      const countryImg = cvm?.[countryImgKey] || getCountryFlag(countryCode);
      if (!countryCode) return placeholder;

      const { countryNameEN, countryNameCN } = getCountryNames(cvm, {
        countryCodeKey,
        countryNameENKey,
        countryNameCNKey,
        countryMap,
      });

      const countryName = getOptionText(locale, {
        textEN: countryNameEN,
        textCN: countryNameCN,
        text: countryNameEN,
        value: countryCode,
      });
      const linkUrl = getLinkUrl?.(cvm) ?? '';

      return (
        <ColumnAligner align={align} width={headerWidth}>
          <ARightClickToJump className="dbi-a-right-click-to-jump" target="_self" href={linkUrl}>
            <div
              className={classnames('dbi-common-column-country', { clickable })}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (!clickable) return;
                onClick?.(countryCode, cvm);
              }}
            >
              <Image
                className="dbi-common-column-country__icon"
                preview={false}
                src={countryImg}
                fallback={DEFAULT_COUNTRY_IMG}
                onError={(e) => {
                  if (e?.target) {
                    // 静态国旗加载模式 默认图片在getCountryFlag方法内做了兜底处理，这里只处理动态加载国旗失败的场景
                    // @ts-ignore
                    e.target.src = DEFAULT_COUNTRY_IMG;
                  }
                }}
              />
              <span className="dbi-common-column-country__text">{countryName}</span>
            </div>
          </ARightClickToJump>
        </ColumnAligner>
      );
    },
  };
};

export default CountryColumnBuilder;
